import { Button, OverlineText, Stack, Text } from "@hopper-ui/components";
import { WarningIcon } from "@hopper-ui/icons";
import {
    GridCardListItem,
    type GridCardListItemRenderFunction,
    uppercaseFirstCharacter,
    useNumberFormatters
} from "@sg-protect/components";
import { MixpanelProperty, useTrackButtonClicked } from "@sg-protect/shell";
import pluralize from "pluralize";
import type { FailedOperation } from "../remediation/BulkProvider.tsx";
import { ACTION_LABELS } from "../../utils/bulkActionLabels.ts";
import { OperationCardHeader } from "./OperationCardHeader.tsx";
import { useDismissOperation } from "./useDismissOperation.ts";
import { useOperationActivityLogHref } from "./useOperationActivityLogHref.ts";

const DANGER_BORDER = { border: "danger" } as const;
const DANGER_LINK_BORDER = { border: "danger", borderHover: "danger-strong" } as const;

const STATUS_LABELS = {
    failure: "Failed",
    partialFailure: "Partial Failure"
} as const satisfies Record<FailedOperation["status"], string>;

export interface FailedOperationCardProps {
    operation: FailedOperation;
}

export function FailedOperationCard({ operation }: FailedOperationCardProps) {
    const { formatNumber } = useNumberFormatters();
    const trackButtonClicked = useTrackButtonClicked();
    const href = useOperationActivityLogHref(operation.bulkId, "failed");
    const { dismiss, isPending: isDismissPending } = useDismissOperation(operation.bulkId);

    const { running: title, entity } = ACTION_LABELS[operation.action];

    const failureMessage =
        operation.errorMessage ??
        `${formatNumber(operation.failedCount)} ${pluralize(entity, operation.failedCount)} failed`;

    const operationMetrics = [
        `${formatNumber(operation.succeededCount)} succeeded`,
        `${formatNumber(operation.failedCount)} failed`
    ];

    // Same values as the backend's BulkOperationProcessed (C# enum names), so the two events join on Action.
    const trackingProperties = {
        [MixpanelProperty.Action]: uppercaseFirstCharacter(operation.action),
        [MixpanelProperty.BulkOperationId]: operation.bulkId,
        [MixpanelProperty.IsAutomationInitiated]: operation.isPolicy,
        [MixpanelProperty.Status]: STATUS_LABELS[operation.status]
    };

    const handleAction = () => {
        trackButtonClicked("Failed Operation Card", "Activity Center", trackingProperties);
        dismiss();
    };

    const handleDismissPress = () => {
        trackButtonClicked("Dismiss Failed Operation", "Activity Center", trackingProperties);
        dismiss();
    };

    const renderCard: GridCardListItemRenderFunction = (props, renderProps, renderBaseCard) =>
        renderBaseCard({ ...props, ...(href ? DANGER_LINK_BORDER : DANGER_BORDER) }, renderProps);

    return (
        <GridCardListItem
            id={operation.bulkId}
            textValue={operation.isPolicy ? `Policy: ${title}` : title}
            href={href}
            onAction={href ? handleAction : undefined}
            padding="inset-md"
            render={renderCard}
        >
            <Stack gap="stack-sm" width="100%">
                <OperationCardHeader
                    title={title}
                    icon={<WarningIcon fill="danger-weak" />}
                    isLink={href !== undefined}
                    isPolicy={operation.isPolicy}
                />
                {operation.status === "failure" && (
                    <Text size="xs" color="neutral-weak" wordBreak="break-word">
                        {failureMessage}
                    </Text>
                )}
                {operation.status === "partialFailure" && (
                    <OverlineText color="neutral-weak" data-public>
                        {operationMetrics.join(" · ")}
                    </OverlineText>
                )}
                <Button
                    variant="ghost-secondary"
                    size="sm"
                    alignSelf="start"
                    onPress={handleDismissPress}
                    isDisabled={isDismissPending}
                    aria-label={`Dismiss ${title}`}
                    data-public
                >
                    Dismiss
                </Button>
            </Stack>
        </GridCardListItem>
    );
}
